import React, { useState, useEffect, useRef } from 'react';
import './LocationSelector.css';

interface LocationSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
}

interface LocationSelectorProps {
  value: string;
  onChange: (location: string) => void;
  placeholder?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Enter your location'
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocations = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&addressdetails=1&limit=5`,
        {
          headers: {
            'User-Agent': 'Lokies-Universe-Dating-App'
          }
        }
      );
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to search locations:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchTerm(query);
    onChange(query);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchLocations(query);
    }, 500);
  };

  const handleSuggestionClick = (suggestion: LocationSuggestion) => {
    const address = suggestion.address;
    let locationName = '';

    if (address) {
      const city = address.city || address.town || address.village;
      const state = address.state;
      const country = address.country;

      if (city && state) {
        locationName = `${city}, ${state}`;
      } else if (city && country) {
        locationName = `${city}, ${country}`;
      } else if (state && country) {
        locationName = `${state}, ${country}`;
      } else {
        locationName = city || state || country || suggestion.display_name;
      }
    } else {
      locationName = suggestion.display_name.split(',').slice(0, 2).join(',');
    }

    setSearchTerm(locationName);
    onChange(locationName);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'Lokies-Universe-Dating-App'
              }
            }
          );
          const data = await response.json();

          if (data && data.address) {
            const address = data.address;
            const city = address.city || address.town || address.village;
            const state = address.state;
            const country = address.country;

            let locationName = '';
            if (city && state) {
              locationName = `${city}, ${state}`;
            } else if (city && country) {
              locationName = `${city}, ${country}`;
            } else if (state && country) {
              locationName = `${state}, ${country}`;
            } else {
              locationName = city || state || country || data.display_name;
            }

            setSearchTerm(locationName);
            onChange(locationName);
          }
        } catch (error) {
          console.error('Failed to get location name:', error);
          alert('Failed to detect your location. Please enter it manually.');
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setDetectingLocation(false);

        let errorMessage = 'Unable to detect your location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'The request timed out.';
            break;
          default:
            errorMessage += 'Please enter your location manually.';
        }
        alert(errorMessage);
      }
    );
  };

  return (
    <div className="location-selector" ref={wrapperRef}>
      <div className="location-input-wrapper">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="location-input"
        />
        <button
          type="button"
          onClick={detectCurrentLocation}
          disabled={detectingLocation}
          className="detect-location-btn"
          title="Detect my location"
        >
          {detectingLocation ? '...' : '📍'}
        </button>
      </div>

      {isLoading && <div className="location-loading">Searching...</div>}

      {showSuggestions && suggestions.length > 0 && (
        <ul className="location-suggestions">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="location-suggestion-item"
            >
              {suggestion.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationSelector;
